"use client";

import { useEffect, useState } from "react";

// How far past the start sentinel the reader must scroll before the bar is
// introduced — approximates "the first section has already scrolled past"
// without needing to know the article's actual content structure.
const START_DELAY_PX = 280;

// `entry.isIntersecting` alone is ambiguous: it's `false` both *before* a
// sentinel/section has ever been reached and *long after* it's been
// scrolled past. Comparing its own position to the root's top edge
// disambiguates: only "above the root top" means it's actually been
// scrolled past, not merely not-yet-reached.
function hasExitedAbove(entry: IntersectionObserverEntry): boolean {
  const rootTop = entry.rootBounds?.top ?? 0;
  return entry.boundingClientRect.top < rootTop;
}

// Drives the SummarizeBar's visibility off two DOM sentinels:
//   - `pastStart`: true once the reader has scrolled past the sentinel at
//     the top of the content column, with a delay so it doesn't trigger on
//     the very first line of content. Needs `hasExitedAbove` so it doesn't
//     flash true for a moment at initial page load (sentinel starts
//     off-screen below, which is also "not intersecting").
//   - `atEnd`: true from the moment "the last section" (FAQ, tags, or
//     whatever the final content block is) crosses 50% visible scrolling
//     down, and stays true for the rest of the page — through the rest of
//     that section, past the resting instance, through ShareButtons/footer —
//     right up until the reader scrolls back up far enough to drop back
//     below 50% via the section's *bottom* edge (i.e. genuinely back before
//     it, in the article). Also needs `hasExitedAbove`: without it, once the
//     section scrolled fully above the viewport (deep in the page), ratio
//     drops back below 0.5 from *that* side too, which would incorrectly
//     clear `atEnd` and bring the bar back for the rest of the page.
// visible = pastStart && !atEnd.
export function useEdgeVisibility(startSentinelId: string, endSentinelId: string): boolean {
  const [pastStart, setPastStart] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const startEl = document.getElementById(startSentinelId);
    const endEl = document.getElementById(endSentinelId);
    if (!startEl || !endEl) return;

    const startObserver = new IntersectionObserver(
      ([entry]) => setPastStart(!entry.isIntersecting && hasExitedAbove(entry)),
      { rootMargin: `-${START_DELAY_PX}px 0px 0px 0px` },
    );
    startObserver.observe(startEl);

    const endObserver = new IntersectionObserver(
      ([entry]) => setAtEnd(entry.isIntersecting || hasExitedAbove(entry)),
      { threshold: 0.5 },
    );
    endObserver.observe(endEl);

    return () => {
      startObserver.disconnect();
      endObserver.disconnect();
    };
  }, [startSentinelId, endSentinelId]);

  return pastStart && !atEnd;
}
